<template>
  <div
    :class="[
      touchPosition,
      {
        'is-touch': isTouchDevice,
        'is-touch-active': isTouchActive,
        'is-hidden': !isVisible
      }
    ]"
    ref="appControls"
    class="ps-app-controls ps-absolute picnic-element is-ready focus:ps-outline-none"
    tabindex="-1"
    @focus="isTouchActive = true"
    @blur="isTouchActive = false"
    @click.stop
  >
    <div
      :class="{ 'ps-shadow-lg': isVisible }"
      class="ps-app-decorator ps-relative ps-rounded-lg"
    >
      <div class="ps-core-buttons ps-rounded">
        <div
          :class="[touchFlex, { 'ps-bg-gray-100': isVisible }]"
          class="core-buttons ps-relative ps-z-20"
        >
          <button-icon
            aria-label="Card List"
            @button-click="handleShowList"
            class="ps-hidden-item picnic-element hint--top"
          >
            <span
              class="ps-w-8 ps-h-8 ps-text-brand-brown-600 hover:ps-text-brand-brown-800"
            >
              <svg-icon name="cards" width="100%" height="100%" />
            </span>
          </button-icon>

          <div class="ps-create-marker ps-relative ps-hidden-item">
            <div class="ps-marker-decorator">
              <div
                ref="plus"
                aria-label="Create Issue"
                @touchend="handleCreateMarkerClick"
                @click="handleCreateMarkerClick"
                :class="{
                  'hint--top': !isTouchActive,
                  'hint--right': isTouchActive
                }"
                class="ps-create-marker picnic-element"
              >
                <span
                  class="ps-dot ps-shadow-md ps-rounded-full ps-shadow-md ps-absolute ps-overflow-hidden ps-block ps-pointer-events-none"
                ></span>
              </div>
            </div>
          </div>

          <button-icon
            aria-label="Toggle Visibility"
            @button-click="toggleVisibility"
            class="ps-toggle-vis ps-text-brand-brown-600 picnic-element hint--top"
          >
            <span class="ps-w-8 ps-h-8">
              <svg-icon
                v-if="isVisible"
                name="eye-off"
                width="100%"
                height="auto"
              />
              <svg-icon v-else name="eye" width="100%" height="auto" />
            </span>
          </button-icon>

          <p
            @click="handleShowList"
            :class="{
              'ps-bg-brand-orange': hasCurrentUserTickets,
              'ps-bg-white': !hasCurrentUserTickets
            }"
            class="ps-ticket-count ps-ml-1 ps-mt-1 ps-w-5 ps-h-5 ps-justify-center ps-absolute ps-pin-t ps-pin-l ps-rounded-full ps-shadow ps-hidden-item ps-cursor-pointer"
          >
            <span
              :class="{
                ' ps-text-white': hasCurrentUserTickets,
                ' ps-text-brand-orange ': !hasCurrentUserTickets
              }"
              class="ps-w-5 ps-h-5 ps-absolute ps-pin-t ps-pin-l ps-flex ps-items-center ps-justify-center ps-text-xs ps-font-bold"
            >
              {{ hasCurrentUserTickets || dotsAssignedPageCount }}
            </span>
          </p>
        </div>

        <button-icon
          aria-label="My Account"
          @button-click="handleShowMe"
          bg-color="brand-brown-400"
          hover-bg-color="brand-brown-800"
          class="ps-slider ps-left ps-text-brand-brown-600 picnic-element hint--top ps-hidden-item"
        >
          <user-avatar class="ps-w-8 ps-h-8" :user="currentUser" />
        </button-icon>
        <button-icon
          aria-label="Question or Support"
          @button-click="handleShowHelp"
          bg-color="brand-brown-400"
          hover-bg-color="brand-brown-800"
          class="ps-slider ps-right ps-text-brand-brown-600 picnic-element hint--top ps-hidden-item"
        >
          <div
            class="ps-h-8 ps-w-8 ps-text-white ps-flex ps-items-center ps-justify-center"
          >
            <svg-icon
              name="question-mark-circle"
              width="100%"
              height="auto"
              :fill="false"
            />
          </div>
        </button-icon>

        <!-- <div class="picnic-controls__touch-activator">
        <div
          aria-label="Show Picnic"
          @touchstart="toggleTouchInterface"
          @click="toggleTouchInterface"
          class="picnic-controls__create-marker picnic-controls__touch-toggle picnic-controls__comment-toggle picnic-element hint--right"
          :class="{ 'picnic-controls__create-marker--text-ready': textReady }"
        >
          <span>+</span>
        </div>
      </div> -->
      </div>
    </div>
    <div
      v-if="isTouchDevice && !isTouchActive && isVisible"
      @touchstart.native="$refs.appControls.el.focus()"
      class="ps-w-full ps-h-full ps-absolute ps-left-0 ps-top-0 ps-z-50"
    ></div>
  </div>
</template>

<script>
import {
  mapGetters,
  mapActions,
  mapState,
  mapMutations
} from '@picnic-software/vuex'
import Draggable from 'draggable'
import Ticket from '@/ticket'
import ButtonIcon from '@/components/buttons/ButtonIcon'
import getSelector from '@/utils/get-selector'
import calculatePosition from '@/utils/calculate-position'
import UserAvatar from '@/components/UserAvatar'

import {
  CARD_MODE_SNAPSHOT,
  CARD_CREATE,
  ROUTE_LIST_PAGE,
  ROUTE_ME,
  ROUTE_HELP
} from '@/common/constants'
import '@/icons/eye'
import '@/icons/eye-off'
import '@/icons/cards'
import '@/icons/cog'
import '@/icons/question-mark-circle'

export default {
  name: 'app-controls',
  components: {
    ButtonIcon,
    UserAvatar
  },
  data() {
    return {
      showOptions: false,
      isDragging: false,
      isTouchActive: false,
      createMarker: {
        dragger: {},
        home: {}
      }
    }
  },
  computed: {
    ...mapState({
      currentUser: state => state.auth.user,
      pageId: state => state.pages.active.id,
      projectId: state => state.provider.projects.active.id,
      isVisible: state => state.app.isVisible,
      isTouchDevice: state => state.app.isTouchDevice
    }),
    ...mapGetters({
      dotsAssignedPageCount: 'pages/dots/count',
      myDotsAssignedPageCount: 'pages/dots/myCount'
    }),

    hasCurrentUserTickets() {
      return this.myDotsAssignedPageCount(this.currentUser.provider_user_id)
    },

    touchPosition() {
      if (this.isTouchDevice) {
        return 'ps-bottom-0 ps-left-1/2 ps-pb-16 ps-pt-8 ps-px-4'
      }
      return 'ps-bottom-0 ps-left-1/2 ps-p-8'
    },
    touchFlex() {
      return 'ps-inline-flex ps-h-full'
    }
  },
  mounted() {
    this.$nextTick(() => this.initDrag())
  },
  methods: {
    ...mapActions({
      newDot: 'dots/new',
      routerCard: 'router/card'
    }),
    ...mapMutations({
      routerTo: 'router/to',
      routerToWithQuery: 'router/toWithQuery',
      toggleVisibility: 'app/toggleIsVisible'
    }),
    initDrag() {
      var options = {
        setCursor: true,
        onDragEnd: this.handleDragEnd,
        onDragStart: (element, x, y, event) => (this.isDragging = true)
      }
      this.createMarker.dragger = new Draggable(this.$refs.plus, options)
      this.createMarker.home = this.createMarker.dragger.get()
    },
    sendMarkerHome() {
      this.createMarker.dragger.set(
        this.createMarker.home.x,
        this.createMarker.home.y
      )
    },
    handleShowList() {
      return this.routerToWithQuery({
        route: ROUTE_LIST_PAGE,
        query: {
          page_id: this.pageId,
          provider_project_id: this.projectId
        }
      })
    },
    handleShowMe() {
      this.routerTo(ROUTE_ME)
    },
    handleShowHelp() {
      this.routerTo(ROUTE_HELP)
    },
    handleToggleOptions() {
      this.$store.dispatch('route', 'settings')
    },
    handleCreateMarkerClick(event) {
      if (this.isDragging) {
        return
      }
      console.log('CREATE CARD FROM CLICK')
      // Check for highlighted text
      // this.$store.dispatch('createHighlight').then(highlight => {
      //   // console.log('highlight archive');
      //   // console.log(highlight)
      //   let createTicket

      //   // Create generic ticket if no highlights present
      //   if (!highlight) {
      this.createGeneralTicket(event)
      //     return
      //   }
      //   this.createEditorialTicket(event, highlight)
      // })
    },
    createGeneralTicket(event) {
      const createTicket = Ticket.createGeneral(event)
      this.dispatchTicket(createTicket)
    },
    createEditorialTicket(event, highlight) {
      const createTicket = Ticket.createEditorial(
        this.$store.getters.pageId,
        event,
        highlight
      )

      // Guard if highlighting failed
      if (!createTicket) {
        this.toaster({ type: 'WARNING', message: 'Text Selection Failed' })
        return
      }
      this.dispatchTicket(createTicket)
      // this.clearExistingTextHighlights()
    },
    handleDragEnd(element, x, y, event) {
      // Guard function:  only trigger if element to drug outside of controls
      // Notify Main app component, which will call function in index file

      console.log('_X', x, '_Y', y, event)

      // Cancel drop if marker doesn't travel 10px
      if (Math.abs(x) < 40 && Math.abs(y) < 40) {
        setTimeout(() => (this.isDragging = false), 400)
        return
      }

      this.sendMarkerHome()
      event.stopImmediatePropagation()
      event.preventDefault()

      // Handle touch events, which don't report .pageX and Y
      const dragCoords = {}
      if (event.clientX) {
        dragCoords.pageX = event.pageX
        dragCoords.pageY = event.pageY
      } else {
        dragCoords.pageX = event.changedTouches[0].pageX
        dragCoords.pageY = event.changedTouches[0].pageY
      }

      let dragTarget = document.elementFromPoint(event.clientX, event.clientY)
      if (!dragTarget || dragTarget.classList.contains('picnic-element')) {
        return console.warn('Whoops.  Drag again')
      }

      // Pass in offset from document to allow ticket to calculate positionoing
      let documentOffset = {
        x: dragCoords.pageX,
        y: dragCoords.pageY
      }

      console.log('all systems go')
      this.dispatchCard(
        this.createSnapshotPayload({
          event: event,
          target: dragTarget,
          documentOffset: documentOffset
        })
      )

      this.sendMarkerHome()

      // hack in a set timeout now, but will need better solution
      setTimeout(() => (this.isDragging = false), 1000)
    },
    createSnapshotPayload({
      event = null,
      target = null,
      documentOffset = null
    }) {
      return {
        dot: {
          selector: getSelector(target),
          position: calculatePosition(target, event, documentOffset),
          mode: CARD_MODE_SNAPSHOT,
          notation: 'box'
        }
      }
    },
    dispatchCard(payload) {
      const { x } = payload.dot.position.fromDocument
      this.newDot(payload)
      this.routerCard({
        id: CARD_CREATE,
        offsetLeft: x
      })

      // Send analytics
      if (this.$ga) {
        this.$ga.event({
          eventCategory: 'ticket',
          eventAction: 'issue started',
          eventLabel: payload.mode
        })
      }
    },
    clearExistingTextHighlights() {
      const loadedHighlights = [
        ...document.querySelectorAll('span.picnic-text-select--is-active')
      ]
      if (loadedHighlights) {
        loadedHighlights.forEach(h => {
          if (h.childNodes.length === 0) {
            h.classList.add('picnic-text-select--is-empty')
          }
        })
      }
    },
    toggleTouchInterface(e) {
      e.stopPropagation()
      e.preventDefault()
      // this.isTouchActive = !this.isTouchActive
      this.$store.dispatch('setVisibility', true)
    },
    clickEvent(e) {
      // e = Mouse click event.
      var rect = e.target.getBoundingClientRect()
      var x = e.clientX - rect.left //x position within the element.
      var y = e.clientY - rect.top //y position within the element.
    }
  }
}

function determineTargetEl(event) {
  let dragCoords = {}

  // dragCoords.pageX = event.changedTouches[0].pageX
  // dragCoords.pageY = event.changedTouches[0].pageY

  if (event.pageX) {
    dragCoords.pageX = event.pageX
    dragCoords.pageY = event.pageY
  }
  // Find what element drag ended on.  Account for page scroll by subtracting window.page{X}Offset
  return document.elementFromPoint(dragCoords.pageX, dragCoords.pageY)
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.ps-app-controls {
  transform: translateX(-50%) rotate(66deg);
  transition: all 350ms ease;
  will-change: transform;
  opacity: 0;
  pointer-events: none !important;
  &.ps-is-active {
    opacity: 0.25;
    transform: translateX(-50%) rotate(0deg);
    pointer-events: auto !important;
    &:hover,
    &:focus {
      opacity: 1;

      & .ps-left {
        transform: translateX(-107%);
      }
      & .ps-right {
        transform: translateX(107%);
      }
      & .ps-core-buttons {
        box-shadow: 0 0 0 6px rgba(78, 61, 66, 0.1),
          0 2.8px 2.2px rgba(0, 0, 0, 0.02), 0 6.7px 5.3px rgba(0, 0, 0, 0.028),
          0 12.5px 10px rgba(0, 0, 0, 0.035),
          0 22.3px 17.9px rgba(0, 0, 0, 0.042),
          0 41.8px 33.4px rgba(0, 0, 0, 0.05), 0 100px 80px rgba(0, 0, 0, 0.07) !important;
      }
    }
  }
  &.is-hidden {
    & .ps-toggle-vis {
      opacity: 0;
    }
    & .ps-hidden-item {
      opacity: 0;
      pointer-events: none !important;
    }
    &:hover {
      & .ps-toggle-vis {
        opacity: 1;
      }
    }
  }
  &.is-touch {
  }
}

.ps-marker-decorator {
  @apply .ps-w-full .ps-h-full;
  background-color: rgba(137, 128, 130, 0.15) !important;
}

.ps-create-marker {
  @apply .ps-relative .ps-h-12 .ps-w-12;
  user-select: none;
  cursor: pointer;
  will-change: transform;

  &:hover {
    & .ps-dot {
      background-color: #ff6f59 !important;
    }
  }
  & .ps-dot {
    background-color: rgb(137, 126, 129) !important;
  }
}

.ps-dot {
  height: 28px;
  width: 28px;
  top: 10px;
  left: 10px;
  user-select: none;
  /* border: 2px solid rgba(#fff, 1) !important; */
}

.ps-slider {
  @apply .ps-absolute .ps-z-10 .ps-top-0 .ps-border-solid .ps-border-brand-brown-400 !important;
  transition: all 350ms ease;
}

.ps-left {
  @apply .ps-rounded-lg .ps-border-l-8 .ps-border-r-8 .ps-left-0 .ps--ml-2 !important;
}

.ps-right {
  @apply .ps-rounded-lg .ps-border-l-8 .ps-border-r-8 .ps-right-0 .ps--mr-2 !important;
}

.picnic--is-touch-device .picnic-controls {
  left: 0;
  right: 50%;
  bottom: auto;
  top: 50vh;
  margin-top: -87.5px;
}
</style>
